import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

// Переводы для разных языков
const translations = {
  ru: {
    title: 'Страница не найдена',
    subtitle: 'К сожалению, запрашиваемая страница не существует или была перемещена. Но не волнуйтесь, мы поможем вам найти то, что вы ищете!',
    homeButton: '🏠 На главную',
    backButton: '← Назад',
    searchPlaceholder: 'Поиск по сайту...',
    searchButton: '🔍 Поиск',
    pathLabel: 'Путь:',
    suggestedLinks: {
      home: 'Главная',
      news: 'Новости',
      questions: 'Вопросы',
      admin: 'Админ панель'
    }
  },
  kz: {
    title: 'Бет табылмады',
    subtitle: 'Өкінішке орай, сұралған бет жоқ немесе жылжытылған. Бірақ алаңдамаңыз, біз сізге іздегеніңізді табуға көмектесеміз!',
    homeButton: '🏠 Басты бетке',
    backButton: '← Артқа',
    searchPlaceholder: 'Сайттан іздеу...',
    searchButton: '🔍 Іздеу',
    pathLabel: 'Жол:',
    suggestedLinks: {
      home: 'Басты бет',
      news: 'Жаңалықтар',
      questions: 'Сұрақтар',
      admin: 'Админ панелі'
    }
  },
  en: {
    title: 'Page Not Found',
    subtitle: 'Unfortunately, the requested page does not exist or has been moved. But don\'t worry, we\'ll help you find what you\'re looking for!',
    homeButton: '🏠 Home',
    backButton: '← Back',
    searchPlaceholder: 'Search the site...',
    searchButton: '🔍 Search',
    pathLabel: 'Path:',
    suggestedLinks: {
      home: 'Home',
      news: 'News',
      questions: 'Questions',
      admin: 'Admin Panel'
    }
  }
};

function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [showPath, setShowPath] = useState(false);

  // Получаем переводы для текущего языка
  const t = translations[currentLanguage] || translations.ru;

  useEffect(() => {
    // Show the error path after a short delay for dramatic effect
    const timer = setTimeout(() => setShowPath(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.length > 1 ? navigate(-1) : navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // You can implement search logic here
      // For now, let's navigate to home with search query
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const suggestedLinks = [
    { label: t.suggestedLinks.home, path: '/' },
    { label: t.suggestedLinks.news, path: '/news' },
    { label: t.suggestedLinks.questions, path: '/questions' },
    { label: t.suggestedLinks.admin, path: '/admin' },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden" 
         style={{ 
           background: `linear-gradient(135deg, var(--primary-color) 0%, var(--primary-light) 50%, var(--secondary-color) 100%)`,
           fontFamily: 'var(--font-family-main)'
         }}>
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-24 h-24 rounded-full animate-bounce opacity-20" 
             style={{ 
               backgroundColor: 'var(--white)',
               animationDelay: '0s', 
               animationDuration: '3s' 
             }}>
        </div>
        <div className="absolute top-20 right-20 w-16 h-16 rounded-full animate-pulse opacity-20" 
             style={{ 
               backgroundColor: 'var(--secondary-color)',
               animationDelay: '1s' 
             }}>
        </div>
        <div className="absolute bottom-20 left-20 w-20 h-20 rounded-full animate-ping opacity-15" 
             style={{ 
               backgroundColor: 'var(--white)',
               animationDelay: '2s' 
             }}>
        </div>
        <div className="absolute bottom-32 right-16 w-12 h-12 rounded-full animate-bounce opacity-20" 
             style={{ 
               backgroundColor: 'var(--secondary-color)',
               animationDelay: '0.5s', 
               animationDuration: '2s' 
             }}>
        </div>
        <div className="absolute top-1/2 left-8 w-28 h-28 rounded-full animate-pulse opacity-10" 
             style={{ 
               backgroundColor: 'var(--white)',
               animationDelay: '1.5s' 
             }}>
        </div>
      </div>

      {/* Main card */}
      <div className="backdrop-blur-lg rounded-3xl p-8 md:p-12 text-center shadow-2xl border max-w-lg w-full relative z-10 animate-fadeInUp"
           style={{
             backgroundColor: 'var(--background-light)',
             borderColor: 'rgba(255, 255, 255, 0.2)',
             boxShadow: 'var(--shadow-md)'
           }}>
        
        {/* 404 Number */}
        <div className="text-8xl md:text-9xl font-black mb-6 animate-pulse"
             style={{
               background: `linear-gradient(45deg, var(--primary-color), var(--primary-light))`,
               WebkitBackgroundClip: 'text',
               WebkitTextFillColor: 'transparent',
               backgroundClip: 'text'
             }}>
          404
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-bold mb-4"
            style={{ 
              color: 'var(--text-dark)',
              fontSize: 'var(--font-size-xxl)'
            }}>
          {t.title}
        </h1>

        {/* Subtitle */}
        <p className="text-lg mb-8 leading-relaxed"
           style={{ 
             color: 'var(--text-dark)',
             fontSize: 'var(--font-size-lg)',
             opacity: '0.8'
           }}>
          {t.subtitle}
        </p>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <button
            onClick={handleGoHome}
            className="px-8 py-3 rounded-full font-semibold transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
            style={{
              background: `linear-gradient(45deg, var(--primary-color), var(--primary-light))`,
              color: 'var(--text-light)',
              borderRadius: 'var(--border-radius-lg)'
            }}
          >
            {t.homeButton}
          </button>
          <button
            onClick={handleGoBack}
            className="border-2 px-8 py-3 rounded-full font-semibold hover:text-white transform hover:-translate-y-1 transition-all duration-300"
            style={{
              borderColor: 'var(--primary-color)',
              color: 'var(--primary-color)',
              borderRadius: 'var(--border-radius-lg)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--primary-color)';
              e.target.style.color = 'var(--text-light)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'var(--primary-color)';
            }}
          >
            {t.backButton}
          </button>
        </div>

        {/* Error path display */}
        {showPath && (
          <div className="mb-6 p-3 border rounded-lg animate-shake"
               style={{
                 backgroundColor: 'rgba(239, 68, 68, 0.1)',
                 borderColor: 'rgba(239, 68, 68, 0.3)',
                 borderRadius: 'var(--border-radius-md)'
               }}>
            <div className="font-mono text-sm break-all"
                 style={{ 
                   color: '#dc2626',
                   fontSize: 'var(--font-size-sm)'
                 }}>
              {t.pathLabel} {location.pathname}
            </div>
          </div>
        )}

      </div>

      {/* Custom CSS for animations not available in Tailwind */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out;
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}

export default NotFoundPage;